import React, { useState, useEffect, useMemo } from 'react';
import { X, Send, CreditCard, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { useCompanyFeeSchedule } from '../../hooks/useFeeSchedules';
import { useLockScroll } from '../../hooks/useLockScroll';
import { calculatePreview, formatCents } from '../../utils/feePreview';
import { ROUTES } from '../../constants/routes';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  access_type: string;
  access_value: number | null;
  billing_cycle: string;
}

interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: () => void;
}

type Step = 'details' | 'choose' | 'select-plan' | 'confirm';

// ─── Phone helpers ───────────────────────────────────────────────────────────

function formatPhoneNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  const hasPlus = input.trim().startsWith('+');

  if (hasPlus) {
    if (digits.length === 0) return '+';
    if (digits.length <= 1) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 1)} ${digits.slice(1)}`;
    if (digits.length <= 7) return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}`;
    if (digits.length <= 11) {
      return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
  }

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  if (digits.length <= 11) {
    return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
}

function extractPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

function validatePhoneNumber(phone: string): boolean {
  if (!phone.trim()) return true;
  const digits = extractPhoneDigits(phone);
  const hasPlus = phone.trim().startsWith('+');
  if (!hasPlus && digits.length === 10) return true;
  if (hasPlus && digits.length >= 10 && digits.length <= 15) return true;
  return false;
}

// ─── Billing helpers ─────────────────────────────────────────────────────────

function formatBillingCycle(cycle: string) {
  switch (cycle) {
    case 'monthly': return 'Monthly';
    case 'quarterly': return 'Quarterly';
    case 'annual': return 'Annual';
    case 'biyearly': return 'Bi-yearly';
    case 'one_time': return 'One-time';
    default: return cycle || 'Monthly';
  }
}

function formatAccess(sub: SubscriptionPlan) {
  if (sub.access_type === 'unlimited') return 'Unlimited';
  return `${sub.access_value} ${sub.access_type}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AddStudentModal({ isOpen, onClose, companyId, onSuccess }: AddStudentModalProps) {
  useLockScroll(isOpen);
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('details');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionPlan | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stripeConnectReady, setStripeConnectReady] = useState<boolean | null>(null);

  const { data: feeSchedule } = useCompanyFeeSchedule(isOpen ? companyId : null);

  // Check if Stripe Connect is configured for this company
  useEffect(() => {
    if (!isOpen || !companyId) return;
    const checkStripeConnect = async () => {
      const { data } = await supabase
        .from('companies')
        .select('stripe_connect_account_id, stripe_connect_account_status, stripe_connect_onboarding_complete')
        .eq('id', companyId)
        .single();
      setStripeConnectReady(
        !!data?.stripe_connect_account_id &&
        data?.stripe_connect_account_status === 'active' &&
        !!data?.stripe_connect_onboarding_complete
      );
    };
    checkStripeConnect();
  }, [isOpen, companyId]);

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setStep('details');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setSelectedSubscription(null);
    setError(null);
    setSuccessMessage(null);
  }, [isOpen]);

  // ── Invoice preview ──────────────────────────────────────────────
  const invoicePreview = useMemo(() => {
    if (!selectedSubscription || !feeSchedule?.items) return null;

    const now = new Date();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const daysRemaining = daysInMonth - now.getDate() + 1;

    const basePriceCents = Math.round(selectedSubscription.price * 100);
    const isProrated = daysRemaining < daysInMonth;
    const proratedBaseCents = isProrated
      ? Math.round((basePriceCents * daysRemaining) / daysInMonth)
      : basePriceCents;

    const preview = calculatePreview(feeSchedule.items, proratedBaseCents);

    return {
      basePriceCents,
      proratedBaseCents,
      isProrated,
      daysRemaining,
      daysInMonth,
      ...preview,
    };
  }, [selectedSubscription, feeSchedule]);

  // ── Load subscriptions ───────────────────────────────────────────
  const loadSubscriptions = async () => {
    setLoadingSubs(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: subError } = await (supabase as any)
        .from('company_subscriptions')
        .select('id, name, price, access_type, access_value, billing_cycle')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (subError) throw subError;
      setSubscriptions(data || []);
    } catch {
      setError('Failed to load subscription plans.');
    } finally {
      setLoadingSubs(false);
    }
  };

  // ── Validate details ─────────────────────────────────────────────
  const validateDetails = (): boolean => {
    setError(null);
    if (!firstName.trim()) { setError('First name is required.'); return false; }
    if (!lastName.trim()) { setError('Last name is required.'); return false; }
    if (!email.trim()) { setError('Email is required.'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address.'); return false; }
    if (phone.trim() && !validatePhoneNumber(phone)) {
      setError('Please enter a valid phone number (10 digits for US, or international format with +).');
      return false;
    }
    return true;
  };

  const handleDetailsNext = () => {
    if (validateDetails()) {
      setStep('choose');
    }
  };

  // ── Send invite (no subscription) ────────────────────────────────
  const handleSendInvite = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      const { data, error: invokeError } = await supabase.functions.invoke('send-direct-invite', {
        body: {
          email: email.trim(),
          companyId,
          adminUserId: currentUser.id,
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          phoneNumber: phone.trim() ? extractPhoneDigits(phone) : null,
        },
      });

      if (invokeError) throw invokeError;

      if (data?.success) {
        setSuccessMessage(data.message || `Invitation sent to ${email.trim()}`);
        onSuccess?.();
        return;
      }

      throw new Error(data?.error || 'Failed to send invite');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send invite. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Create & enroll with billing ─────────────────────────────────
  const handleConfirmEnrollment = async () => {
    if (!selectedSubscription) return;

    setSubmitting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: invokeError } = await supabase.functions.invoke('create-student-and-enrollment-checkout', {
        body: {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim() || undefined,
          subscriptionId: selectedSubscription.id,
          companyId,
        },
      });

      if (invokeError) throw invokeError;
      if (!data?.success) throw new Error(data?.error || 'Failed to create checkout session');

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start enrollment. Please try again.';
      // Detect Stripe Connect not configured and redirect to onboarding
      if (message.toLowerCase().includes('stripe connect') || message.toLowerCase().includes('billing is not configured')) {
        setError('Stripe is not set up for your gym. Redirecting to billing setup...');
        setTimeout(() => {
          onClose();
          navigate(ROUTES.ONBOARDING_BILLING);
        }, 1500);
      } else {
        setError(message);
      }
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const studentName = `${firstName.trim()} ${lastName.trim()}`.trim();

  // Step count for progress bar
  const totalSteps = step === 'select-plan' || step === 'confirm' ? 4 : 3;
  const currentStep = step === 'details' ? 1 : step === 'choose' ? 2 : step === 'select-plan' ? 3 : 4;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#EBFFEE]">
            {step === 'details' && 'Add Student'}
            {step === 'choose' && 'How would you like to add them?'}
            {step === 'select-plan' && 'Select Subscription Plan'}
            {step === 'confirm' && 'Confirm Enrollment'}
          </h3>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5 mb-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded ${i < currentStep ? 'bg-[#9B1421]' : 'bg-[#504A4A]'}`}
            />
          ))}
        </div>

        {/* Success state */}
        {successMessage && (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-green-600/15 flex items-center justify-center mx-auto mb-4">
              <Send className="w-7 h-7 text-green-400" />
            </div>
            <h4 className="text-lg font-semibold text-[#EBFFEE] mb-2">Invite Sent!</h4>
            <p className="text-sm text-[#A9A3A3] mb-6">{successMessage}</p>
            <Button
              onClick={onClose}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
            >
              Done
            </Button>
          </div>
        )}

        {/* Error */}
        {!successMessage && error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* ── Step 1: Student details ──────────────────────────────── */}
        {!successMessage && step === 'details' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-1">First Name *</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
                className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-1">Last Name *</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
                className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(formatPhoneNumber(e.target.value));
                  if (error?.includes('phone')) setError(null);
                }}
                placeholder="(503) 555-1234 (optional)"
                maxLength={20}
                className={`w-full px-3 py-2 bg-[#363232] border rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421] ${
                  phone.trim() && !validatePhoneNumber(phone) ? 'border-red-500' : 'border-[#504A4A]'
                }`}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={onClose}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDetailsNext}
                className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Choose path ──────────────────────────────────── */}
        {!successMessage && step === 'choose' && (
          <div className="space-y-3">
            <p className="text-sm text-[#A9A3A3] mb-4">
              Adding <span className="text-[#EBFFEE] font-medium">{studentName}</span> ({email.trim()})
            </p>

            {/* Invite only */}
            <button
              onClick={handleSendInvite}
              disabled={submitting}
              className="w-full bg-[#363232] hover:bg-[#504A4A] border border-[#504A4A] rounded-lg p-4 text-left transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-600/15 flex items-center justify-center shrink-0">
                  <Send className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-[#EBFFEE] font-semibold">Send Invite Only</p>
                  <p className="text-[#A9A3A3] text-xs mt-1">
                    Invite them to join without a subscription. Great for children, trial members, or when the parent handles billing.
                  </p>
                </div>
              </div>
              {submitting && (
                <div className="flex items-center gap-2 mt-3 text-[#A9A3A3] text-sm">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-[#A9A3A3]" />
                  Sending invite...
                </div>
              )}
            </button>

            {/* Enroll with subscription */}
            {stripeConnectReady === false ? (
              <button
                onClick={() => {
                  onClose();
                  navigate(ROUTES.ONBOARDING_BILLING);
                }}
                className="w-full bg-[#363232] hover:bg-[#504A4A] border border-yellow-600/40 rounded-lg p-4 text-left transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-600/15 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-[#EBFFEE] font-semibold">Enroll with Subscription</p>
                    <p className="text-yellow-400 text-xs mt-1">
                      Stripe is not set up yet. Tap here to connect your Stripe account before enrolling students with billing.
                    </p>
                  </div>
                </div>
              </button>
            ) : (
              <button
                onClick={() => {
                  setStep('select-plan');
                  loadSubscriptions();
                }}
                disabled={submitting}
                className="w-full bg-[#363232] hover:bg-[#504A4A] border border-[#504A4A] rounded-lg p-4 text-left transition-colors disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center shrink-0">
                    <CreditCard className="w-5 h-5 text-[#EBFFEE]" />
                  </div>
                  <div>
                    <p className="text-[#EBFFEE] font-semibold">Enroll with Subscription</p>
                    <p className="text-[#A9A3A3] text-xs mt-1">
                      Create their account and set up billing with a subscription plan via Stripe.
                    </p>
                  </div>
                </div>
              </button>
            )}

            <div className="pt-2">
              <Button
                onClick={() => { setStep('details'); setError(null); }}
                disabled={submitting}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Select subscription ──────────────────────────── */}
        {!successMessage && step === 'select-plan' && (
          <>
            {loadingSubs ? (
              <p className="text-[#A9A3A3] text-center py-8">Loading subscriptions...</p>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#A9A3A3]">No subscriptions available.</p>
                <p className="text-sm text-[#A9A3A3] mt-2">Create a subscription plan first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => {
                      setSelectedSubscription(sub);
                      setStep('confirm');
                    }}
                    className="w-full bg-[#363232] hover:bg-[#504A4A] border border-[#504A4A] rounded-lg p-4 text-left transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-[#EBFFEE] font-semibold">{sub.name}</p>
                        <p className="text-[#A9A3A3] text-sm mt-1">
                          ${Number(sub.price).toFixed(2)} / {formatBillingCycle(sub.billing_cycle)}
                        </p>
                        <p className="text-[#A9A3A3] text-xs mt-1">{formatAccess(sub)}</p>
                      </div>
                      <span className="text-[#A9A3A3]">&gt;</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-4">
              <Button
                onClick={() => { setStep('choose'); setError(null); }}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
            </div>
          </>
        )}

        {/* ── Step 4: Confirm enrollment ───────────────────────────── */}
        {!successMessage && step === 'confirm' && selectedSubscription && (
          <div className="space-y-4">
            {/* Student + plan details */}
            <div className="bg-[#363232] rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Student</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{studentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Email</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{email.trim()}</span>
              </div>
              {phone.trim() && (
                <div className="flex justify-between">
                  <span className="text-[#A9A3A3] text-sm">Phone</span>
                  <span className="text-[#EBFFEE] text-sm font-medium">{phone.trim()}</span>
                </div>
              )}
              <div className="border-t border-[#504A4A] my-2" />
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Plan</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{selectedSubscription.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Start Date</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Price</span>
                <span className="text-[#EBFFEE] text-sm font-medium">
                  ${Number(selectedSubscription.price).toFixed(2)} / {formatBillingCycle(selectedSubscription.billing_cycle)}
                </span>
              </div>
            </div>

            {/* Invoice preview */}
            {invoicePreview && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Invoice Preview</label>
                <div className="space-y-1.5">
                  {invoicePreview.isProrated ? (
                    <>
                      <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                        <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                        <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.basePriceCents)}</span>
                      </div>
                      <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                        <span className="text-[#A9A3A3] text-sm">
                          Prorated ({invoicePreview.daysRemaining}/{invoicePreview.daysInMonth} days)
                        </span>
                        <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.proratedBaseCents)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                      <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                      <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.proratedBaseCents)}</span>
                    </div>
                  )}
                  {invoicePreview.lineItems
                    .filter((li: { chargeEffect: string; amountCents: number }) => li.chargeEffect === 'add' && li.amountCents > 0)
                    .map((li: { heading: string; amountCents: number }, idx: number) => (
                      <div key={idx} className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                        <span className="text-[#A9A3A3] text-sm">{li.heading}</span>
                        <span className="text-[#EBFFEE] text-sm">+{formatCents(li.amountCents)}</span>
                      </div>
                    ))}
                  <div className="flex justify-between items-center px-3 py-2 bg-[#504A4A] rounded-md font-semibold">
                    <span className="text-[#EBFFEE] text-sm">Due today</span>
                    <span className="text-red-400 text-sm">{formatCents(invoicePreview.payerCents)}</span>
                  </div>
                </div>
              </div>
            )}

            <p className="text-[#A9A3A3] text-xs">
              A new account will be created for this student. You will be redirected to Stripe
              to enter their payment details.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={() => { setStep('select-plan'); setError(null); }}
                disabled={submitting}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
              <Button
                onClick={handleConfirmEnrollment}
                disabled={submitting}
                className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </span>
                ) : (
                  'Create & Proceed to Payment'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
